import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, ArrowLeft, Pencil, Check, X } from "lucide-react";

const statusLabels: Record<string, string> = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
};
const statusColors: Record<string, string> = {
  pending: "bg-warning/10 text-warning",
  approved: "bg-success/10 text-success",
  rejected: "bg-destructive/10 text-destructive",
};

function BoolField({ label, value, details }: { label: string; value: boolean | null; details?: string | null }) {
  if (value === null || value === undefined) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      {value ? <Check className="h-4 w-4 text-success mt-0.5" /> : <X className="h-4 w-4 text-muted-foreground mt-0.5" />}
      <div>
        <span className="font-medium">{label}</span>
        {value && details && <p className="text-muted-foreground text-xs mt-0.5">{details}</p>}
      </div>
    </div>
  );
}

export default function AccountReviewDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();

  const { data: review, isLoading } = useQuery({
    queryKey: ["my-review-detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*, venues(name, slug, city)")
        .eq("id", id!)
        .eq("user_id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!id,
  });

  if (loading || isLoading) {
    return <Layout><div className="container py-8"><Skeleton className="h-64" /></div></Layout>;
  }
  if (!user) return <Navigate to="/auth" />;
  if (!review) return <Layout><div className="container py-8"><p>Review no encontrada.</p></div></Layout>;

  const venue = review.venues as any;
  const r = review as any;

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        <Link to="/mi-cuenta/reviews" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Volver a mis reviews
        </Link>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-xl">
                {venue?.name ?? "Sala"}
              </CardTitle>
              <Badge className={statusColors[review.status ?? "pending"]}>
                {statusLabels[review.status ?? "pending"]}
              </Badge>
            </div>
            {venue?.city && <p className="text-sm text-muted-foreground">{venue.city}</p>}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Ratings */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">General</p>
                <p className="flex items-center gap-1 text-lg font-bold">
                  <Star className="h-4 w-4 fill-warning text-warning" />
                  {Number(review.overall_rating).toFixed(1)}
                </p>
              </div>
              {review.sound_rating && (
                <div>
                  <p className="text-xs text-muted-foreground">Sonido</p>
                  <p className="flex items-center gap-1 text-lg font-bold">
                    <Star className="h-4 w-4 fill-warning text-warning" />
                    {Number(review.sound_rating).toFixed(1)}
                  </p>
                </div>
              )}
              {review.staff_rating && (
                <div>
                  <p className="text-xs text-muted-foreground">Staff</p>
                  <p className="flex items-center gap-1 text-lg font-bold">
                    <Star className="h-4 w-4 fill-warning text-warning" />
                    {Number(review.staff_rating).toFixed(1)}
                  </p>
                </div>
              )}
            </div>

            {/* Event data */}
            <div className="grid gap-3 sm:grid-cols-2 text-sm">
              {review.event_date && (
                <div>
                  <span className="text-muted-foreground">Fecha evento: </span>
                  {new Date(review.event_date).toLocaleDateString("es-ES")}
                </div>
              )}
              {review.price_paid != null && (
                <div>
                  <span className="text-muted-foreground">Precio pagado: </span>
                  {review.price_paid}€
                </div>
              )}
            </div>

            {/* Operational */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Información operativa</h3>
              <BoolField label="Técnico de sonido propio" value={review.sound_tech} />
              <BoolField label="Rider técnico" value={review.technical_rider} details={review.technical_rider_notes} />
              <BoolField label="Zona de merchandising" value={review.merchandising_area} details={r.merch_area_details} />
              <BoolField label="Ticketing obligatorio" value={review.ticketing_required} details={review.ticketing_provider} />
            </div>

            {/* Comment */}
            {review.comment && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Experiencia</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{review.comment}</p>
              </div>
            )}

            {/* Dates & edit info */}
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground border-t pt-4">
              <span>Enviada: {new Date(review.created_at).toLocaleDateString("es-ES")}</span>
              {r.is_edited && r.edited_at && (
                <span className="flex items-center gap-1">
                  <Pencil className="h-3 w-3" />
                  Editada: {new Date(r.edited_at).toLocaleDateString("es-ES")}
                </span>
              )}
            </div>

            {/* Rejection reason */}
            {review.status === "rejected" && review.rejection_reason && (
              <div className="rounded-md bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive">Motivo de rechazo:</p>
                <p className="text-muted-foreground">{review.rejection_reason}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to={`/sala/${venue?.slug}/`}>Ver sala</Link>
              </Button>
              <Button asChild size="sm">
                <Link to={`/mi-cuenta/reviews/${review.id}/editar`}>
                  <Pencil className="mr-1 h-3 w-3" /> Editar
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
